import React, { useState, useRef, useEffect } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Terminal, Send, Loader2, Sparkles, ShieldAlert } from "lucide-react";

const COMMANDS = [
  "Show all players born in 2014",
  "Find all unpaid players",
  "How much money is owed this month?",
  "Which category has the most players?",
  "Show missing documents",
  "Find duplicate player records",
  "Give me a financial summary",
  "Show players with attendance below 70%",
  "Give me a summary of this month's academy activity",
];

export default function AICommandCenter() {
  const [items, setItems] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items, loading]);

  const run = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setItems((c) => [...c, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const r = await client.post("/ai/assistant", { query: q });
      setItems((c) => [...c, { role: "ai", text: r.data.reply }]);
    } catch (e) {
      toast.error(apiError(e));
      setItems((c) => [...c, { role: "ai", text: "Sorry, I couldn't process that command." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="AI Command Center" subtitle="Query your academy in natural language — grounded on live data" testid="ai-command-page" />
      <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2 text-sm">
        <ShieldAlert size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <span>The Command Center analyzes and reports on your data only. Destructive actions (deleting or bulk-changing records) must be confirmed and performed manually from their modules for safety.</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {COMMANDS.map((c) => (
          <button key={c} onClick={() => run(c)} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-emerald-500/10 hover:border-emerald-500/40 transition-colors" data-testid={`cmd-${c.slice(0, 10)}`}>{c}</button>
        ))}
      </div>
      <Card className="p-0 overflow-hidden">
        <div className="h-[45vh] overflow-y-auto scrollbar-thin p-5 space-y-4">
          {items.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-3">
              <div className="h-14 w-14 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center"><Terminal size={26} /></div>
              <p>Type a command or pick a suggestion above.</p>
            </div>
          )}
          {items.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-emerald-600 text-white" : "bg-muted"}`}>
                {m.role === "ai" && <Sparkles size={14} className="inline me-1 text-emerald-600" />}{m.text}
              </div>
            </div>
          ))}
          {loading && <div className="flex justify-start"><div className="bg-muted rounded-2xl px-4 py-2.5"><Loader2 className="animate-spin" size={16} /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-border p-3 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. Show all U13 players with unpaid fees..." data-testid="cmd-input" />
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => run()} disabled={loading} data-testid="cmd-send"><Send size={16} /></Button>
        </div>
      </Card>
    </div>
  );
}
